import ProductCard from '@components/cards/ProductCard';
import awaitUserLogin from '@components/login/awaitUserLogin';
import TaggedProducts from '@components/tagged-products/TaggedProducts';
import UserContext from '@context/UserContext';
import IconClear from '@mui/icons-material/Clear';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import PhotoLightBox from '@ui/components/photo-lightbox/PhotoLightBox';
import TaggedProductsImage from '@ui/components/tagged-products-image/TaggedProductsImage';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import { useStyles } from '@ui/utils/makeStyles';
import shortAddress from '@ui/utils/shortAddress';
import slugOrID from '@ui/utils/slugOrID';
import { addSavedProduct, removeSavedProduct } from '@utils/savedItems';
import Link from 'next/link';
import React, { useContext, useState } from 'react';
import { A11y, Mousewheel, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';

interface PhotosCarouselProps {
  className?: string;
  style?: React.CSSProperties
  photos?: guesthouse.Photo[];
  onClick?: (e, photo: guesthouse.Photo) => void;
  SwiperProps?: SwiperProps;
}

const PhotosCarousel: React.FC<PhotosCarouselProps> = (props) => {
  const { photos, className, style, onClick, SwiperProps } = props;
  const userContext = useContext<UserContext>(UserContext);
  const { theme } = useStyles();
  const { md } = useBreakpoints();
  const [selectedPhoto, setSelectedPhoto] = useState<string>();
  const [lightboxVisible, setLightboxVisible] = useState(false);

  return (
    <>
      <Swiper
        navigation
        className={className}
        style={style}
        modules={[Pagination, Navigation, A11y, Mousewheel]}
        observer={true}
        observeParents={true}
        mousewheel={{
          forceToAxis: true,
        }}
        freeMode={true}
        slidesPerView="auto"
        spaceBetween={24}
        pagination={{
          clickable: true,
          type: 'progressbar',
        }}
        {...SwiperProps}
      >
        {!!photos?.length && photos.map(photo => {
          if (!photo) {
            return null;
          }

          const height = md ? 300 : 150;
          const width = Math.floor((photo.width / photo.height) * height) || height;

          return (
            <SwiperSlide
              key={photo.docID}
              style={{ width }}
            >
              <img
                key={photo.docID}
                src={photo.large_url}
                style={{
                  height,
                  width,
                  objectFit: 'cover',
                  borderRadius: theme.shape.borderRadius,
                  cursor: 'pointer',
                }}
                onClick={(e) => {

                  if (typeof onClick === 'function') {
                    onClick(e, photo);
                  } else {
                    setSelectedPhoto(photo.docID);
                    setLightboxVisible(true);
                  }
                }}
              />
            </SwiperSlide>
          );
        })}
      </Swiper>

      <Dialog
        fullScreen
        open={lightboxVisible}
        maxWidth={false}
        style={{
          height: '100%',
        }}
        PaperProps={{
          style: {
            height: '100%',
          }
        }}
        onClose={() => setLightboxVisible(false)}
      >
        <PhotoLightBox
          photos={photos}
          selectedPhotoID={selectedPhoto}
          setSelectedPhotoID={setSelectedPhoto}
          renderImage={({ photo }) => {
            if (photo) {
              return (
                <TaggedProductsImage
                  photoTagsPath={`homes/${photo?.home?.docID}/photos/${photo.docID}/productTags`}
                  photo={photo}
                  src={photo.large_url}
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'contain',
                  }}
                  renderTooltip={(productTag) => {
                    const { product } = productTag;
                    const saved = userContext.savedProducts?.ids.indexOf(product.docID) > -1;

                    return (
                      <ProductCard
                        style={{
                          color: theme.palette.common.black,
                        }}
                        product={product as guesthouse.Product}
                        saved={saved}
                        onSavedClick={async (e) => {
                          e.stopPropagation();

                          if (userContext.user) {
                            if (saved) {
                              removeSavedProduct(userContext.user.uid, product as guesthouse.Product);
                            } else {
                              addSavedProduct(userContext.user.uid, product as guesthouse.Product);
                            }
                          } else {
                            await awaitUserLogin(userContext)
                              .then((user) => {
                                addSavedProduct(user.uid, product as guesthouse.Product);
                              });
                          }
                        }}
                      />
                    );
                  }}
                />
              );
            }
          }}
          renderInteractions={() => {
            return (
              <Button
                variant="text"
                color="white"
                size="small"
                startIcon={<IconClear />}
                style={{ marginRight: theme.spacing(2) }}
                onClick={() => {
                  setLightboxVisible(false);
                }}
              >
                Close
              </Button>
            );
          }}
          renderSidebar={({ photo }) => {
            let title = '';
            let url = '';

            if (photo?.home) {
              title = shortAddress(photo?.home?.address);
              url = `/shop/homes/${slugOrID(photo.home)}`;

              if (photo.room) {
                title += ` (${photo.room.title})`;
              }
            }


            return (
              <>
                {title && (
                <Link
                  passHref
                  legacyBehavior
                  href={url}
                >
                  <Typography
                    variant="h5Alt"
                    component="a"
                    style={{
                      fontWeight: 600,
                      marginBottom: theme.spacing(1),
                      fontSize: 18,
                      color: theme.palette.common.black,
                      textDecoration: 'none',
                    }}
                  >
                    {title}
                  </Typography>
                </Link>
                )}
                <TaggedProducts photo={photo} />
              </>
            );
          }}
        />
      </Dialog>
    </>
  );
};

PhotosCarousel.defaultProps = {
  SwiperProps: {}
};

export default PhotosCarousel;
